import { Suspense } from 'react';
import { Await, createRoute, getRouteApi, redirect } from '@tanstack/react-router';
import { useSuspenseQuery } from '@tanstack/react-query';
import { zodValidator } from '@tanstack/zod-adapter';
import { GeneralSpinner, LiliPlanType } from '@lili/lili-frontend-common';
import { useBoundStore } from '@Store/useBoundStore';
import { GTMEventLabel } from '@Services/analytics/types';
import { dataLayerHandler } from '@Services/analytics/analytics.service';
import { RoutesMap } from '@Router/router.types';
import { routesModel } from '@Router/router.consts';
import { beforeLoadGeneral } from '@Router/middleware';
import { ExperienceMap, VariationName, VARIATION_NAME_TO_KEY_MAP } from '@Services/convert/types';
import { loadExperiencesQuery } from '@Services/convert/useExperience';
import { planSelectionQueryParamsSchema } from '@Pages/happyFlowPages/planSelection/queryParamsSchema';
import { getPlansQueryOptions } from '@Pages/happyFlowPages/planSelection/api/planSelection.api';
import { NonUSSocurePage } from '@Pages/happyFlowPages/NonUsSocure/NonUSSocurePage';
import { AccountOpeningReason } from '@Pages/happyFlowPages/accountOpeningReason/accountOpeningReason';
import { AutoLoginPage } from '@Pages/autoLogin/AutoLoginPage';
import { webAppLogin } from '@Pages/autoLogin/api/autoLogin.api';
import { sendValidationMessage } from '@Hooks/api/sendSocureValidationMessage.api';
import { fetchCountries } from '@Hooks/api/fetchCountries.api';
import { SplashScreen } from '@Components/splash/SplashScreen';
import { OnboardingFlowIdentifier } from '@Types/onboarding.types';
import { initializeSigmaDevice } from '@Services/analytics/socure.service';
import { router } from '@Routes/__root';
import { VerificationFailed } from '@Pages/verificationFailed/VerificationFailed';
import { KycFailed, OneOfUs, PlanSelectionPage, ReviewPage, SuccessPage } from '..';
import { rootRoute } from './rootRoute';

const planSelectionRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: routesModel[RoutesMap.PLAN_SELECTION].path,
  beforeLoad: async ({ context, search }) => {
    beforeLoadGeneral({ page: RoutesMap.PLAN_SELECTION, shouldUpdateServerNavStack: true });

    const [plansData, experiences] = await Promise.all([
      context.queryClient.ensureQueryData(getPlansQueryOptions),
      context.queryClient.ensureQueryData(loadExperiencesQuery),
    ]);
    // Priority (highest last, so last assignment wins):
    // CORE fallback → CORE experiment → API defaultPlan → ACCOUNTANT_AS_OWNER
    let effectiveDefault = LiliPlanType.CORE;

    const corePlanAsDefaultExperience = experiences.find(
      (e) => e.id === ExperienceMap.CORE_PLAN_AS_DEFAULT
    );
    if (
      corePlanAsDefaultExperience?.variationKey ===
      VARIATION_NAME_TO_KEY_MAP[VariationName.DEFAULT_CORE_PLAN]
    ) {
      effectiveDefault = LiliPlanType.CORE;
    }

    if (plansData?.defaultPlan) {
      effectiveDefault = plansData.defaultPlan;
    }

    const onboardingFlowIdentifier =
      useBoundStore.getState().onboardingSlice.onboardingFlowIdentifier;

    if (onboardingFlowIdentifier === OnboardingFlowIdentifier.ACCOUNTANT_AS_OWNER) {
      effectiveDefault = LiliPlanType.PREMIUM;
    }

    if (search.initialPlan !== effectiveDefault) {
      throw redirect({
        to: routesModel[RoutesMap.PLAN_SELECTION].path,
        search: { initialPlan: effectiveDefault },
      });
    }
  },
  component: () => (
    <Suspense fallback={<GeneralSpinner />}>
      <PlanSelectionPage />
    </Suspense>
  ),
  validateSearch: zodValidator(planSelectionQueryParamsSchema),
});

const ReviewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: routesModel[RoutesMap.REVIEW].path,
  beforeLoad: async ({ context }) => {
    dataLayerHandler(GTMEventLabel.CONFIRM_SCREEN);
    const isAppSubmitted = useBoundStore.getState().onboardingSlice.isAppSubmitted;
    if (isAppSubmitted) {
      router.navigate({ to: routesModel[RoutesMap.WELCOME].path });
    }
    beforeLoadGeneral({
      page: RoutesMap.REVIEW,
      shouldUpdateServerNavStack: true,
      stepToReturn: RoutesMap.PLAN_SELECTION,
    });
    initializeSigmaDevice();
    await context.queryClient.prefetchQuery({
      queryKey: ['countries', true, true],
      queryFn: () => fetchCountries(true, true),
      staleTime: Infinity,
    });
  },

  loader: async () => fetchCountries(true, true),
  component: () => (
    <Suspense fallback={<GeneralSpinner />}>
      <ReviewPage />
    </Suspense>
  ),
});

const SuccessRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: routesModel[RoutesMap.SUCCESS].path,
  beforeLoad: () => {
    beforeLoadGeneral({ page: RoutesMap.SUCCESS, shouldUpdateServerNavStack: false });
  },
  loader: async () => {
    const isMobile = window.innerWidth <= 768;
    const password = useBoundStore.getState().customer.customerData?.password;
    const isEmbedded = useBoundStore.getState().onboardingSlice.getIsEmbedded();
    const isMultiBusiness =
      useBoundStore.getState().onboardingSlice.onboardingFlowIdentifier ===
      OnboardingFlowIdentifier.DIRECT_MULTI_BUSINESS;
    if (!isMobile && password && !isEmbedded && !isMultiBusiness) {
      throw redirect({ to: routesModel[RoutesMap.AUTO_LOGIN].path });
    }
  },
  component: () => <SuccessPage />,
});
const SuccessRouteWithoutAutoLogin = createRoute({
  getParentRoute: () => rootRoute,
  path: routesModel[RoutesMap.SUCCESS_WITHOUT_AUTO_LOGIN].path,
  beforeLoad: () => {
    beforeLoadGeneral({
      page: RoutesMap.SUCCESS_WITHOUT_AUTO_LOGIN,
      shouldUpdateServerNavStack: false,
    });
  },
  loader: async () => {},
  component: () => <SuccessPage />,
});

const AccountOpeningReasonRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: routesModel[RoutesMap.ACCOUNT_OPENING_REASON].path,
  beforeLoad: () => {
    beforeLoadGeneral({
      page: RoutesMap.ACCOUNT_OPENING_REASON,
      shouldUpdateServerNavStack: false,
    });
  },
  component: () => (
    <Suspense fallback={<GeneralSpinner />}>
      <AccountOpeningReason />
    </Suspense>
  ),
});

const createSocureDataQuery = (customerId: string) => ({
  queryKey: ['getSocureData', customerId],
  queryFn: () => sendValidationMessage(customerId),
});

const NonUSSocurePageRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: routesModel[RoutesMap.NON_US_SOCURE].path,
  loader: async ({ context }) => {
    const customerId = useBoundStore.getState().customer.customerId ?? '';
    await context.queryClient.ensureQueryData(createSocureDataQuery(customerId));
  },
  beforeLoad: () => {
    beforeLoadGeneral({
      page: RoutesMap.NON_US_SOCURE,
      shouldUpdateServerNavStack: false,
    });
  },
  component: function NonUsSocurePageFunc() {
    const customerId = useBoundStore((state) => state.customer.customerId) ?? '';
    const socureDataQuery = createSocureDataQuery(customerId);
    const { data: socureData } = useSuspenseQuery(socureDataQuery);
    return <NonUSSocurePage key={'nonuskey'} socureData={socureData} />;
  },
  wrapInSuspense: true,
  pendingComponent: () => <GeneralSpinner />,
});

const AutoLoginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: routesModel[RoutesMap.AUTO_LOGIN].path,
  beforeLoad: () => {
    beforeLoadGeneral({
      page: RoutesMap.AUTO_LOGIN,
      shouldUpdateServerNavStack: false,
    });
  },
  loader: async () => {
    try {
      const customerData = useBoundStore.getState().customer.customerData;
      if (!customerData || !customerData.email || !customerData.password) {
        throw new Error('Customer data is missing');
      }
      const result = webAppLogin({
        loginName: customerData.email,
        password: customerData.password,
      });
      return { webAppResponseData: result };
    } catch {
      console.error('Redirecting to SuccessPage due to login failure');
      throw redirect({ to: routesModel[RoutesMap.SUCCESS_WITHOUT_AUTO_LOGIN].path });
    }
  },
  component: () => {
    const routeApi = getRouteApi(routesModel[RoutesMap.AUTO_LOGIN].path);
    const webAppResponseData = routeApi.useLoaderData()?.webAppResponseData;
    return (
      <Await
        promise={Promise.resolve(webAppResponseData).catch(() => {
          console.error('Redirecting to SuccessPage due to login failure');
          router.navigate({ to: routesModel[RoutesMap.SUCCESS_WITHOUT_AUTO_LOGIN].path });
          return null; // Ensure a value is returned
        })}
        fallback={<SplashScreen />}>
        {(data) => {
          return <AutoLoginPage webAppResponseData={data} />;
        }}
      </Await>
    );
  },
});

const oneOfUsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: routesModel[RoutesMap.ONE_OF_US].path,
  beforeLoad: () => {
    beforeLoadGeneral({ page: RoutesMap.ONE_OF_US, shouldUpdateServerNavStack: false });
  },
  component: () => (
    <Suspense fallback={<GeneralSpinner />}>
      <OneOfUs />
    </Suspense>
  ),
});

const kycFailedRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: routesModel[RoutesMap.KYC_FAILED].path,
  beforeLoad: () => {
    beforeLoadGeneral({ page: RoutesMap.KYC_FAILED, shouldUpdateServerNavStack: false });
  },
  component: () => (
    <Suspense fallback={<GeneralSpinner />}>
      <KycFailed />
    </Suspense>
  ),
});

const VerificationFailedRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: routesModel[RoutesMap.VERIFICATION_FAILED].path,
  component: () => <VerificationFailed />,
  beforeLoad: () => {
    beforeLoadGeneral({
      page: RoutesMap.VERIFICATION_FAILED,
      shouldUpdateServerNavStack: false,
      shouldAvoidAuthCheck: true,
    });
  },
});

export const finishingRoutes = [
  AccountOpeningReasonRoute,
  planSelectionRoute,
  ReviewRoute,
  SuccessRoute,
  SuccessRouteWithoutAutoLogin,
  NonUSSocurePageRoute,
  AutoLoginRoute,
  oneOfUsRoute,
  kycFailedRoute,
  VerificationFailedRoute,
];
